/**
 * Das Detailfenster einer Aufgabe oder Phase - Portierung von `renderTaskDetailPanel`
 * (`index.html:9059-9127`), aufgerufen ueber `openTaskDetail`/`openTaskPhaseDetail`
 * (`:9011-9024`) und geschlossen ueber `closeTaskDetail` (`:9026-9031`).
 *
 * ================= WARUM `createPortal` =================
 *
 * Der Bestand haengt das Panel per `d.insertAdjacentHTML('beforeend', ...)` an `#drawer` selbst an -
 * als GESCHWISTER von `.drawer-body`, `.drawer-head` und `.drawer-foot` (`:9118`). `Drawer.tsx`
 * (Task 16, "nicht anfassen") setzt `children` dagegen zwingend INNERHALB von `.drawer-body`. Ohne
 * Portal liesse sich diese Position nicht nachbilden, ausser man akzeptiert eine strukturelle
 * Abweichung. Diese Komponente sucht deshalb das bereits gerenderte `#drawer`-Element (das
 * `Drawer.tsx` traegt) und portalt ihren Inhalt dorthin - `useLayoutEffect`, weil das Ziel erst NACH
 * dem ersten Commit im echten DOM steht (Portal und `children` haengen im selben React-Baum, das
 * `#drawer`-Element committet also gleichzeitig mit dieser Komponente, nicht davor).
 *
 * Rendert `<TasksTab>` OHNE ein `#drawer`-Element im DOM (z. B. ein Komponententest, der nur den
 * Reiterinhalt zeichnet), liefert dieser Sucher `null` und die Komponente zeichnet NICHTS - exakt
 * die Bestandslogik `const d = $('#drawer'); ... if (!d || ...) return;` (`:9060-9064`).
 *
 * ================= BEFUND: das Panel unterliegt dem Rechte-Durchgang NIE ======================
 *
 * `Drawer.tsx`s Rechte-Durchgang (Task 16) erfasst ausschliesslich
 * `.drawer-body input, .drawer-body select, .drawer-body textarea, .drawer-body button`
 * (`index.html:9299`). Weil das Detail-Panel ein GESCHWISTER von `.drawer-body` ist (nicht dessen
 * Kind), erreicht der Durchgang keines seiner Bedienelemente - unabhaengig vom Rechtesatz bleiben
 * sie IMMER bedienbar. `renderTaskDetailPanel` selbst prueft an keiner Stelle `hasPermission`. Das
 * ist eine Luecke im Bestand (fuer `manage_tasks` verhaelt sich das Tab-Recht damit anders als fuer
 * jeden anderen Reiter), aber die Wortlichkeitsregel verlangt: GEMELDET, nicht repariert. Ein
 * Nachbau, der hier eine eigene `disabled={!rechte.has('manage_tasks')}`-Pruefung einzoege, erfaende
 * eine zweite Rechtequelle, die es im Original nicht gibt - siehe Task-Bericht.
 */
import { createPortal } from "react-dom";
import { useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";
import { Zuweisung } from "./Zuweisung.tsx";
import { taskBelongsToPhase, taskPhaseDefinition, taskPhaseDetail } from "./PhasenListe.tsx";
import { fmtDate, NUM } from "../../../lib/format.ts";
import type { Zustaendigkeitsmodul } from "../../../api/zustaendigkeitsModule.ts";
import type { Order, OrderTask } from "../../../lib/types.ts";

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../../Ikone.tsx";

/** `TASK_DETAIL_VIEW` (index.html:4593), hier lokal statt als Modul-Global - siehe `TasksTab.tsx`. */
export type AufgabenAnsicht = { kind: "task"; index: number } | { kind: "phase"; phaseId: string } | null;

interface FreitextFeldProps {
  feld: "name" | "description";
  wert: string;
  platzhalter: string;
  className?: string;
  mehrzeilig?: boolean;
  onFeldAendern: (schluessel: string, wert: unknown) => void;
}

/**
 * Ein Freitextfeld der Aufgabe, das beim VERLASSEN schreibt - nicht bei jedem Tastendruck.
 *
 * ================= DIE ABWEICHUNG VOM BESTAND UND IHR ZWINGENDER GRUND =========================
 *
 * Der Bestand haengt an beiden Feldern ein `oninput` (index.html:9079, 9086) und schreibt bei JEDEM
 * Tastendruck durch. Das ging dort, weil `save()` den ganzen Datenbestand lokal hielt und die
 * Fassung ohne Versionszaehler auskam.
 *
 * Hier waere dasselbe ein garantierter Fehlschlag, kein theoretischer: `PATCH .../tasks/:taskId`
 * traegt die `rowVersion` DER AUFGABE, und jeder erfolgreiche Schreibvorgang erhoeht sie
 * (`updateVersioned`). Beim Tippen von "Hallo" gingen fuenf Anfragen in etwa einer Sekunde hinaus,
 * die zweite bis fuenfte alle mit der Version, die die erste bereits verbraucht hat - vier 409er und
 * vier Toasts, waehrend der Nutzer noch schreibt. Die Aenderung ankommen wuerde nur der erste
 * Buchstabe.
 *
 * Deshalb: UNKONTROLLIERT tippen (`defaultValue`), schreiben bei `blur`. Genau ein Schreibvorgang je
 * Bearbeitung, genau eine Version.
 *
 * `key` aus Aufgaben-Id UND Feldname: ein unkontrolliertes Feld uebernimmt einen von aussen
 * geaenderten Wert NICHT - beim Wechsel auf eine ANDERE Aufgabe muss es deshalb neu aufgebaut
 * werden, sonst stuende der Text der vorigen darin. Innerhalb DERSELBEN Aufgabe bleibt der Tippstand
 * bewusst stehen, auch wenn zwischendurch nachgeladen wird: einem Schreibenden den Text unter den
 * Haenden auszutauschen ist schlimmer als eine Sekunde veraltete Anzeige.
 *
 * Der Vergleich vor dem Melden ist kein Zierrat: `blur` feuert auch beim blossen Durchklicken. Ohne
 * ihn erhoehte jedes Anfassen des Feldes die `rowVersion` und schriebe einen Verlaufseintrag fuer
 * eine Aenderung, die niemand gemacht hat.
 */
function FreitextFeld({
  feld,
  wert,
  platzhalter,
  className,
  mehrzeilig,
  onFeldAendern,
}: FreitextFeldProps) {
  const beiVerlassen = (neu: string): void => {
    if (neu === wert) return;
    onFeldAendern(feld, neu);
  };
  // `key` steht am AUFRUF (`<FreitextFeld key={…}>`), nicht hier: ein `key` in einem gespreizten
  // Props-Objekt ist kein Reconciler-Schluessel, sondern eine gewoehnliche Eigenschaft - React
  // warnt darueber ausdruecklich, und der Neuaufbau bliebe aus.
  return mehrzeilig ? (
    <textarea
      defaultValue={wert}
      placeholder={platzhalter}
      onBlur={(e) => beiVerlassen(e.currentTarget.value)}
    />
  ) : (
    <input
      className={className}
      defaultValue={wert}
      placeholder={platzhalter}
      onBlur={(e) => beiVerlassen(e.currentTarget.value)}
    />
  );
}

export interface AufgabenDetailProps {
  bestellung: Order;
  ansicht: AufgabenAnsicht;
  /**
   * Die ECHTEN Zustaendigkeitsmodule (`GET /api/responsibility-modules`), nicht die Modulform des
   * Bestands aus der Aufgabenvorlage - siehe `TasksTab.tsx`, Abschnitt "Die Zustaendigkeitsmodule
   * laedt dieser Reiter selbst".
   */
  module: readonly Zustaendigkeitsmodul[];
  /**
   * Sperrt Name/Beschreibung/Zuweisung/Frist der Phase SELBST (`taskPhaseDetails`) - dafuer gibt es
   * keine Route (siehe `TasksTab.tsx`). Betrifft NICHT mehr die "Phase erledigt"-Checkbox - siehe
   * `onPhaseErledigt` unten.
   */
  phasenGesperrt: boolean;
  /** `closeTaskDetail()` (index.html:9026-9031), vereinfacht: siehe TasksTab.tsx. */
  onSchliessen: () => void;
  /** `patchOpenTaskDetail(key, value)` (index.html:9033-9050) - nur noch im Aufgaben-Fall. */
  onFeldAendern: (schluessel: string, wert: unknown) => void;
  /**
   * `patchTaskResponsibility` - nur im Aufgaben-Fall. Liefert die MODUL-ID, nicht mehr die fertig
   * zugewiesene Aufgabenkopie: Hauptverantwortung und Vertretung leitet der Server daraus ab
   * (`uebertrageVerantwortung`), siehe `api/aufgaben.ts#KEIN_AUFGABEN_WEG`.
   */
  onZuweisen: (modulId: string) => void;
  /** `deleteOpenTaskDetail()` (index.html:9052-9057) - nur im Aufgaben-Fall. */
  onLoeschen: () => void;
  /**
   * Auftrag "Nachbesserung Phasen-Checkbox" (Pruefer-Befund 2): dieselbe Bulk-Route wie die
   * Phasen-Checkbox der Listenansicht (`PhasenListe.tsx#TaskPhaseBlock`, `onPhaseErledigt`) -
   * `PATCH .../tasks/bulk-done` setzt `done` fuer ALLE Tasks der Phase auf einmal. Die zweite
   * "Phase erledigt"-Checkbox HIER im Detailfenster ist NICHT Teil von `taskPhaseDetails` (sie
   * schreibt `order_tasks.done`, nicht die gesperrten Phasenangaben) und war deshalb faelschlich
   * an `phasenGesperrt` gebunden.
   */
  onPhaseErledigt: (taskIds: string[], erledigt: boolean) => void;
}

export function AufgabenDetail({
  bestellung: o,
  ansicht,
  module,
  phasenGesperrt,
  onSchliessen,
  onFeldAendern,
  onZuweisen,
  onLoeschen,
  onPhaseErledigt,
}: AufgabenDetailProps) {
  const [wurzel, setWurzel] = useState<Element | null>(null);
  useLayoutEffect(() => {
    setWurzel(document.getElementById("drawer"));
  });

  if (!wurzel || !ansicht) return null;

  const orderLabel = (o.orderNo as string) || (o.name as string) || "Bestellung";
  const tasks = (o.tasks as OrderTask[] | undefined) || [];

  let inhalt: ReactNode;

  if (ansicht.kind === "task") {
    const aufgabe = tasks[ansicht.index];
    if (!aufgabe) return null;
    const phase = taskPhaseDefinition(o, (aufgabe.phaseId as string) || "custom");
    inhalt = (
      <>
        <div className="task-detail-kind">Aufgabe · {taskPhaseDetail(o, phase).name}</div>
        <div className="task-detail-title-row">
          <input
            className="task-detail-done"
            type="checkbox"
            checked={!!aufgabe.done}
            aria-label="Aufgabe erledigt"
            onChange={(e) => onFeldAendern("done", e.currentTarget.checked)}
          />
          <FreitextFeld
            key={`${aufgabe.id as string}-name`}
            className="task-detail-title"
            feld="name"
            wert={(aufgabe.name as string) ?? ""}
            platzhalter="Aufgabenname"
            onFeldAendern={onFeldAendern}
          />
        </div>
        <div className="task-detail-properties">
          <Zuweisung aufgabe={aufgabe} module={module} onZuweisen={onZuweisen} />
          <label>Fälligkeitsdatum</label>
          {/*
            `onChange` bleibt: ein Datumsfeld liefert EIN Ereignis je Auswahl, keine Kette wie eine
            Tastatureingabe - das Wettlaufproblem aus `FreitextFeld` unten entsteht hier nicht.
          */}
          <input
            type="date"
            value={(aufgabe.due as string) ?? ""}
            onChange={(e) => onFeldAendern("due", e.currentTarget.value)}
          />
        </div>
        <div className="task-detail-description">
          <label>Beschreibung</label>
          <FreitextFeld
            key={`${aufgabe.id as string}-description`}
            mehrzeilig
            feld="description"
            wert={(aufgabe.description as string) ?? ""}
            platzhalter="Beschreibung, Links oder Hinweise…"
            onFeldAendern={onFeldAendern}
          />
        </div>
        {/*
          ABWEICHUNG VOM BESTAND, benannt statt still: dort stand hier "Bearbeitet von <Name>"
          (index.html:9089), weil `updatedBy` ein freier Anzeigename war. Im neuen Modell ist es ein
          Fremdschluessel auf `users` (005_domaene.sql:174) - an dieser Stelle stuende also eine
          nackte uuid. Die SPA kann sie nicht aufloesen: sie laedt keine Nutzerliste (die gibt es nur
          hinter `manage_users`), und den ANGEMELDETEN Nutzer stattdessen hinzuschreiben waere eine
          Behauptung ueber jemand anderen.
          Deshalb der Zeitpunkt statt des Namens - eine wahre Angabe weniger ist besser als eine
          unwahre mehr. Wer den Namen zurueckwill, braucht zuerst einen Leseweg fuer Nutzer.
        */}
        <div className="task-detail-footer">
          <span>
            {aufgabe.updatedAt
              ? `Zuletzt bearbeitet am ${fmtDate(String(aufgabe.updatedAt).slice(0, 10))}`
              : "Noch nicht bearbeitet"}
          </span>
          <button className="task-detail-delete" type="button" onClick={onLoeschen}>
            <Ikone name="trash-2" />
            Aufgabe löschen
          </button>
        </div>
      </>
    );
  } else {
    const phase = taskPhaseDefinition(o, ansicht.phaseId);
    const detail = taskPhaseDetail(o, phase);
    const entries = tasks.filter((task) => taskBelongsToPhase(task, phase.id));
    const phaseDone = entries.length ? entries.every((task) => !!task.done) : detail.done;
    /*
     * DER PHASEN-ZWEIG IST GESPERRT, NICHT ENTFERNT.
     *
     * Name, Beschreibung, Verantwortlicher und Frist der Phase SELBST schreiben `taskPhaseDetails`.
     * Die Tabelle dafuer gibt es (`order_task_phase_details`, 005_domaene.sql:185), eine Route dafuer
     * nicht - kein Treffer fuer `phase_details` in `backend/src/http`. Bis zum 2026-08-15 lief jeder
     * Tastendruck hier in `FeldOhneGruppeFehler` und damit in den Toast "Die Bestellung konnte nicht
     * geändert werden.": bedienbar, laut, wirkungslos.
     *
     * LESEN bleibt vollstaendig - Name, Beschreibung, Verantwortlicher, Frist und Fortschritt der
     * Phase stehen weiter da. Nur geaendert werden koennen sie nicht, und das sieht man ihnen jetzt
     * an. Wer sie schreibbar machen will, braucht zuerst die Route; der Rest steht hier bereit.
     *
     * DIE "PHASE ERLEDIGT"-CHECKBOX GEHOERT NICHT DAZU (Nachbesserung, Pruefer-Befund 2): `done`
     * steht in `order_tasks`, nicht in `taskPhaseDetails` - dieselbe Bulk-Route wie die Checkbox der
     * Listenansicht (`PhasenListe.tsx#TaskPhaseBlock`) traegt sie. Sie bleibt deshalb bedienbar, auch
     * waehrend `phasenGesperrt` die uebrigen Felder unten sperrt.
     */
    const sperrTitel = "Für Phasenangaben gibt es keine Route - siehe TasksTab.tsx";
    inhalt = (
      <>
        <div className="task-detail-kind milestone">
          {phase.id === "general" || phase.id === "custom" ? "Bereich" : "Meilenstein"}
        </div>
        <div className="task-detail-title-row">
          {/*
            Auftrag "Meilensteine (Phasen) im Tasks-Tab abhakbar machen", Nachbesserung
            (Pruefer-Befund 2): KEIN `disabled={phasenGesperrt}` mehr - `done` gehoert nicht zu den
            gesperrten Phasenangaben (siehe Kommentar oben). `onChange` sammelt dieselben Task-IDs
            wie `taskIdsDerGruppe()` in `PhasenListe.tsx#TaskPhaseBlock` und meldet sie ueber
            `onPhaseErledigt` an denselben Bulk-Weg (`PATCH .../tasks/bulk-done`).
          */}
          <input
            className="task-detail-done"
            type="checkbox"
            checked={phaseDone}
            aria-label="Phase erledigt"
            title={phaseDone ? "Phase wieder öffnen" : "Alle Tasks dieser Phase erledigen"}
            onChange={(e) => {
              const taskIds = entries.map((task) => String(task.id ?? "")).filter((id) => id.length > 0);
              if (taskIds.length === 0) return;
              onPhaseErledigt(taskIds, e.currentTarget.checked);
            }}
          />
          <input
            className="task-detail-title"
            value={detail.name}
            placeholder="Phasenname"
            readOnly={phasenGesperrt}
            aria-readonly={phasenGesperrt ? "true" : undefined}
            title={phasenGesperrt ? sperrTitel : undefined}
            onInput={phasenGesperrt ? undefined : (e) => onFeldAendern("name", e.currentTarget.value)}
            onChange={() => {}}
          />
        </div>
        <div className="task-detail-properties">
          <label>Verantwortlich</label>
          <input
            list="taskAssignees"
            value={detail.assignee}
            placeholder="Keine verantwortliche Person"
            readOnly={phasenGesperrt}
            aria-readonly={phasenGesperrt ? "true" : undefined}
            title={phasenGesperrt ? sperrTitel : undefined}
            onInput={
              phasenGesperrt ? undefined : (e) => onFeldAendern("assignee", e.currentTarget.value)
            }
            onChange={() => {}}
          />
          <label>Fälligkeitsdatum</label>
          {/* Ein Datumsfeld ignoriert `readOnly` in mehreren Browsern - hier deshalb `disabled`. */}
          <input
            type="date"
            value={detail.due}
            disabled={phasenGesperrt}
            title={phasenGesperrt ? sperrTitel : undefined}
            onChange={phasenGesperrt ? () => {} : (e) => onFeldAendern("due", e.currentTarget.value)}
          />
          <label>Fortschritt</label>
          <div className="task-detail-progress">
            {NUM.format(entries.filter((task) => task.done).length)} von {NUM.format(entries.length)} Aufgaben
            erledigt
          </div>
        </div>
        <div className="task-detail-description">
          <label>Beschreibung</label>
          <textarea
            placeholder="Beschreibung oder Hinweise…"
            value={detail.description}
            readOnly={phasenGesperrt}
            aria-readonly={phasenGesperrt ? "true" : undefined}
            title={phasenGesperrt ? sperrTitel : undefined}
            onInput={
              phasenGesperrt ? undefined : (e) => onFeldAendern("description", e.currentTarget.value)
            }
            onChange={() => {}}
          />
        </div>
      </>
    );
  }

  return createPortal(
    <div className="task-detail-panel" role="dialog" aria-label="Task-Details">
      <div className="task-detail-head">
        <button
          className="task-detail-back"
          type="button"
          onClick={onSchliessen}
          aria-label="Zurück zur Aufgabenliste"
        >
          <Ikone name="arrow-left" />
        </button>
        <div className="task-detail-crumb">
          <span>{orderLabel}</span>
          <strong>{ansicht.kind === "task" ? "Aufgabendetails" : "Phasendetails"}</strong>
        </div>
        <button
          className="icon-btn"
          type="button"
          onClick={onSchliessen}
          aria-label="Detailfenster schließen"
        >
          <Ikone name="x" />
        </button>
      </div>
      <div className="task-detail-body">{inhalt}</div>
    </div>,
    wurzel,
  );
}
